import * as i0 from '@angular/core';
import { EventEmitter, TemplateRef, ViewContainerRef, Component, Inject, ViewChildren, HostListener, Directive, Input, Output, InjectionToken, Optional, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, NgModule } from '@angular/core';
import * as i1$1 from '@anthem/uxd/util';
import { ESCAPE, appendNextToLocation, TooltipDef, focusTrap, getFocusableBoundaryElements } from '@anthem/uxd/util';
import * as i1 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i1$2 from '@angular/cdk/overlay';
import { Overlay, OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { trigger, state, style, transition, animate, keyframes } from '@angular/animations';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i2 from '@angular/cdk/layout';
import * as i3 from '@angular/cdk/a11y';
import { normalizePassiveListenerOptions } from '@angular/cdk/platform';

//istanbul ignore next
class PositionService {
    /**
     * Provides read-only equivalent of jQuery's position function:
     * http://api.jquery.com/position/
     */
    position(nativeEl) {
        let elBCR = this.offset(nativeEl);
        let offsetParentBCR = { top: 0, left: 0 };
        let offsetParentEl = this.parentOffsetEl(nativeEl);
        if (offsetParentEl !== this.document) {
            offsetParentBCR = this.offset(offsetParentEl);
            offsetParentBCR.top += offsetParentEl.clientTop - offsetParentEl.scrollTop;
            offsetParentBCR.left += offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
        }
        let boundingClientRect = nativeEl.getBoundingClientRect();
        return {
            width: boundingClientRect.width || nativeEl.offsetWidth,
            height: boundingClientRect.height || nativeEl.offsetHeight,
            top: elBCR.top - offsetParentBCR.top,
            left: elBCR.left - offsetParentBCR.left
        };
    }
    /**
     * Provides read-only equivalent of jQuery's offset function:
     * http://api.jquery.com/offset/
     */
    offset(nativeEl) {
        let boundingClientRect = nativeEl.getBoundingClientRect();
        return {
            width: boundingClientRect.width || nativeEl.offsetWidth,
            height: boundingClientRect.height || nativeEl.offsetHeight,
            top: boundingClientRect.top + (window.pageYOffset || this.document.documentElement.scrollTop),
            left: boundingClientRect.left + (window.pageXOffset || this.document.documentElement.scrollLeft)
        };
    }
    /**
     * Provides coordinates for the targetEl in relation to hostEl
     */
    positionElements(hostEl, targetEl, positionStr, appendToBody) {
        let positionStrParts = positionStr.split('-');
        let pos0 = positionStrParts[0];
        let pos1 = positionStrParts[1] || 'center';
        let hostElPos = appendToBody ? this.offset(hostEl) : this.position(hostEl);
        let targetElWidth = targetEl.offsetWidth;
        let targetElHeight = targetEl.offsetHeight;
        let shiftWidth = {
            center: () => {
                return hostElPos.left + hostElPos.width / 2 - targetElWidth / 2;
            },
            left: () => {
                return hostElPos.left;
            },
            right: () => {
                return hostElPos.left + hostElPos.width;
            }
        };
        let shiftHeight = {
            center: () => {
                return hostElPos.top + hostElPos.height / 2 - targetElHeight / 2;
            },
            top: () => {
                return hostElPos.top;
            },
            bottom: () => {
                return hostElPos.top + hostElPos.height;
            }
        };
        let targetElPos;
        switch (pos0) {
            case 'right':
                targetElPos = {
                    top: shiftHeight[pos1](),
                    left: shiftWidth[pos0]()
                };
                break;
            case 'left':
                targetElPos = {
                    top: shiftHeight[pos1](),
                    left: hostElPos.left - targetElWidth
                };
                break;
            case 'bottom':
                targetElPos = {
                    top: shiftHeight[pos0](),
                    left: shiftWidth[pos1]()
                };
                break;
            default:
                targetElPos = {
                    top: hostElPos.top - targetElHeight,
                    left: shiftWidth[pos1]()
                };
                break;
        }
        return targetElPos;
    }
    get document() {
        return window.document;
    }
    getStyle(nativeEl, cssProp) {
        // IE
        if (nativeEl.currentStyle) {
            return nativeEl.currentStyle[cssProp];
        }
        if (window.getComputedStyle) {
            return window.getComputedStyle(nativeEl)[cssProp];
        }
        // finally try and get inline style
        return nativeEl.style[cssProp];
    }
    /**
     * Checks if a given element is statically positioned
     * @param nativeEl - raw DOM element
     */
    isStaticPositioned(nativeEl) {
        return (this.getStyle(nativeEl, 'position') || 'static') === 'static';
    }
    /**
     * returns the closest, non-statically positioned parentOffset of a given
     * element
     * @param nativeEl
     */
    parentOffsetEl(nativeEl) {
        let offsetParent = nativeEl.offsetParent || this.document;
        while (offsetParent && offsetParent !== this.document && this.isStaticPositioned(offsetParent)) {
            offsetParent = offsetParent.offsetParent;
        }
        return offsetParent || this.document;
    }
}
const positionService = new PositionService();

//istanbul ignore next
class TooltipOptions {
    constructor(options) {
        Object.assign(this, options);
    }
}

//istanbul ignore next
class TooltipContainerComponent {
    constructor(element, cdr, options) {
        this.element = element;
        /* tslint:disable */
        this.manualHideEvent = new EventEmitter();
        this.top = '-1000px';
        this.left = '-1000px';
        this.right = 'auto';
        this.indicatorLeft = '50%';
        this.indicatorRight = 'auto';
        this.display = 'block';
        this.calculatePositionFlag = false;
        this.element = element;
        this.cdr = cdr;
        Object.assign(this, options);
        this.classMap = { in: false, fade: false };
        this.classMap[options.placement] = true;
        this.classMap['tooltip-' + options.placement] = true;
    }
    ngAfterViewInit() {
        if (this.placement === 'bottom') {
            this.indicatorLeft = 'calc(50% - 1em)';
        }
        let p = positionService.positionElements(this.hostEl.nativeElement, this.element.nativeElement.children[0], this.placement || 'left', this.appendToBody);
        this.top = p.top + 'px';
        this.left = p.left + 'px';
        this.classMap.in = true;
        if (this.animation) {
            this.classMap.fade = true;
        }
        if (this.popupClass) {
            this.classMap[this.popupClass] = true;
        }
        this.cdr.detectChanges();
        if (this.hasCloseBtn && this.autoFocus) {
            this.closeBtn.first.element.nativeElement.focus();
        }
        else {
            //TODO
            //this.mainContent.first.element.nativeElement.focus();
        }
        let leftToolTipRect = this.element.nativeElement.children[0].getBoundingClientRect().left;
        if (leftToolTipRect < 0) {
            let elementRect = this.hostEl.nativeElement.getBoundingClientRect();
            let leftParentPos = this.hostEl.nativeElement.parentElement.getBoundingClientRect().left;
            let indicatorPosition = elementRect.width / 2 + elementRect.left - leftParentPos;
            if (this.placement === 'bottom') {
                this.indicatorLeft = 'calc(' + indicatorPosition + 'px - 1em)';
            }
            else {
                this.indicatorLeft = indicatorPosition + 'px';
            }
            this.left = 'auto';
        }
        let multipleLinesFlag = this.hostEl.nativeElement.getClientRects ? this.hostEl.nativeElement.getClientRects().length > 1 : false;
        if (multipleLinesFlag) {
            this.left = 'auto';
            let rectArray = this.hostEl.nativeElement.getClientRects();
            if (this.placement === 'top') {
                this.right = '15px';
                this.indicatorLeft = 'auto';
                this.toolTipToPopOverSpace = this.element.nativeElement.children[0].getBoundingClientRect().right - this.hostEl.nativeElement.getClientRects()[0].right;
                this.calculatePositionFlag = true;
            }
            else if (this.placement === 'bottom') {
                this.indicatorLeft = 'calc(' + rectArray[rectArray.length - 1].width / 2 + 'px - .75em)';
                this.indicatorRight = 'auto';
            }
        }
    }
    ngAfterViewChecked() {
        if (this.calculatePositionFlag) {
            let oldValue = this.toolTipToPopOverSpace;
            this.toolTipToPopOverSpace = this.element.nativeElement.children[0].getBoundingClientRect().right - this.hostEl.nativeElement.getClientRects()[0].right;
            if (oldValue !== this.toolTipToPopOverSpace) {
                this.calculatePositionFlag = false;
                this.indicatorRight = this.hostEl.nativeElement.getClientRects()[0].width / 2 + this.toolTipToPopOverSpace + 'px';
            }
        }
    }
    get isTemplate() {
        return this.htmlContent instanceof TemplateRef;
    }
    manualHide(focusHost = true) {
        // IE11 async-hack to ensure focus returns to the tooltip icons after close.
        window.setTimeout(() => this.hostEl.nativeElement.focus(), 1);
        this.manualHideEvent.emit();
    }
    onContentKeydown(e) {
        let key = e.which || e.keyCode;
        if (key === ESCAPE) {
            this.manualHide();
        }
    }
    onDocumentClick(event) {
        this.autoCloseOnBlur(event, false);
    }
    onDocumentKeydown(event) {
        this.autoCloseOnBlur(event, true);
    }
    autoCloseOnBlur(event, focusHost) {
        if (event.target !== this.hostEl.nativeElement && !this.hostEl.nativeElement.contains(event.target) && !this.element.nativeElement.contains(event.target) && this.classMap.in) {
            this.manualHide(focusHost);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TooltipContainerComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: TooltipOptions }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: TooltipContainerComponent, selector: "tooltip-container", host: { listeners: { "document:click": "onDocumentClick($event)", "document:keydown": "onDocumentKeydown($event)" } }, viewQueries: [{ propertyName: "closeBtn", predicate: ["tooltipCloseBtn"], descendants: true, read: ViewContainerRef }, { propertyName: "mainContent", predicate: ["tooltipContent"], descendants: true, read: ViewContainerRef }], ngImport: i0, template: `<div class="popover ant-tooltip-component" role="tooltip"
              [ngStyle]="{top: top, left: left, right: right, display: display}"
              [ngClass]="classMap">
                <div class="popover-close" *ngIf="hasCloseBtn"><a #tooltipCloseBtn aria-label="close tooltip" href="javascript:void(0)" (click)="manualHide($event)" data-autofocus=""><span class="tooltip-remove-icon fa fa-remove"></span></a></div>
                <div class="popover-content"
                    *ngIf="htmlContent && !isTemplate"
                    innerHtml="{{htmlContent}}" [tabindex]="autoFocus?0:-1" (keydown)="onContentKeydown($event)">
                </div>
                <div #tooltipContent class="popover-content" (focusout)="manualHide($event)"
                    *ngIf="htmlContent && isTemplate" [tabindex]="autoFocus?0:-1" (keydown)="onContentKeydown($event)">
                  <ng-template *ngTemplateOutlet="htmlContent; context: {model: context}" >
                  </ng-template>
                </div>
                <div class="popover-content"
                    *ngIf="content" [tabindex]="autoFocus?0:-1" (keydown)="onContentKeydown($event)">
                  {{content}}
                </div>
                <span [ngStyle]="{left: indicatorLeft, right: indicatorRight}" class="popover-indicator" aria-hidden="true"></span>
              </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TooltipContainerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tooltip-container',
                    // changeDetection: ChangeDetectionStrategy.OnPush,
                    template: `<div class="popover ant-tooltip-component" role="tooltip"
              [ngStyle]="{top: top, left: left, right: right, display: display}"
              [ngClass]="classMap">
                <div class="popover-close" *ngIf="hasCloseBtn"><a #tooltipCloseBtn aria-label="close tooltip" href="javascript:void(0)" (click)="manualHide($event)" data-autofocus=""><span class="tooltip-remove-icon fa fa-remove"></span></a></div>
                <div class="popover-content"
                    *ngIf="htmlContent && !isTemplate"
                    innerHtml="{{htmlContent}}" [tabindex]="autoFocus?0:-1" (keydown)="onContentKeydown($event)">
                </div>
                <div #tooltipContent class="popover-content" (focusout)="manualHide($event)"
                    *ngIf="htmlContent && isTemplate" [tabindex]="autoFocus?0:-1" (keydown)="onContentKeydown($event)">
                  <ng-template *ngTemplateOutlet="htmlContent; context: {model: context}" >
                  </ng-template>
                </div>
                <div class="popover-content"
                    *ngIf="content" [tabindex]="autoFocus?0:-1" (keydown)="onContentKeydown($event)">
                  {{content}}
                </div>
                <span [ngStyle]="{left: indicatorLeft, right: indicatorRight}" class="popover-indicator" aria-hidden="true"></span>
              </div>`
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: TooltipOptions, decorators: [{
                    type: Inject,
                    args: [TooltipOptions]
                }] }]; }, propDecorators: { closeBtn: [{
                type: ViewChildren,
                args: ['tooltipCloseBtn', { read: ViewContainerRef }]
            }], mainContent: [{
                type: ViewChildren,
                args: ['tooltipContent', { read: ViewContainerRef }]
            }], onDocumentClick: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }], onDocumentKeydown: [{
                type: HostListener,
                args: ['document:keydown', ['$event']]
            }] } });

//istanbul ignore next
/* tslint:disable */
/* tslint:enable */
class TooltipDirective {
    constructor(viewContainerRef, uxHelper, componentFactoryResolver, changeDetectorRef) {
        this.viewContainerRef = viewContainerRef;
        this.uxHelper = uxHelper;
        this.componentFactoryResolver = componentFactoryResolver;
        this.changeDetectorRef = changeDetectorRef;
        this.placement = 'top';
        this.enable = true;
        this.animation = true;
        this.appendToBody = false;
        this.delay = 0;
        this.hasCloseBtn = true;
        this.autoFocus = true;
        this.openOnFocus = false;
        /* tslint:enable */
        this.tooltipStateChanged = new EventEmitter();
        // protected changeDetectorRef: ChangeDetectorRef;
        this.visible = false;
    }
    ngOnDestroy() {
        if (this._subscription1) {
            this._subscription1.unsubscribe();
        }
    }
    onFocus(e) {
        if (this.openOnFocus) {
            this.show();
        }
    }
    // todo: filter triggers
    // params: event, target
    show() {
        if (this.visible || !this.enable || this.delayTimeoutId) {
            return;
        }
        const showTooltip = () => {
            this.visible = true;
            let options = new TooltipOptions({
                content: this.content,
                htmlContent: this.htmlContent,
                placement: this.placement,
                animation: this.animation,
                appendToBody: this.appendToBody,
                hostEl: this.viewContainerRef.element,
                popupClass: this.popupClass,
                context: this.tooltipContext,
                hasCloseBtn: this.hasCloseBtn,
                autoFocus: this.autoFocus
            });
            if (this.appendToBody) {
                this.tooltip = this.uxHelper.appendNextToRoot(TooltipContainerComponent, TooltipOptions, options);
            }
            else {
                this.tooltip = appendNextToLocation(TooltipContainerComponent, this.viewContainerRef, this.componentFactoryResolver, [{ provide: TooltipOptions, useValue: options }]);
            }
            if (this.tooltip) {
                this._subscription1 = this.tooltip.instance.manualHideEvent.subscribe((focusHost) => {
                    this.hide(focusHost);
                });
            }
            this.changeDetectorRef.markForCheck();
            this.triggerStateChanged();
        };
        if (this.delay) {
            this.delayTimeoutId = setTimeout(() => {
                showTooltip();
            }, this.delay);
        }
        else {
            showTooltip();
        }
    }
    // params event, target
    // @HostListener('focusout')
    // @HostListener('mouseleave')
    hide(focusHost) {
        if (this.delayTimeoutId) {
            clearTimeout(this.delayTimeoutId);
            this.delayTimeoutId = undefined;
        }
        if (!this.visible) {
            return;
        }
        this.visible = false;
        this.tooltip.destroy();
        this.triggerStateChanged();
        if (focusHost) {
            this.viewContainerRef.element.nativeElement.focus();
        }
    }
    triggerStateChanged() {
        if (this.tooltipStateChanged) {
            this.tooltipStateChanged.emit(this.visible);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TooltipDirective, deps: [{ token: i0.ViewContainerRef }, { token: i1$1.UxHelper }, { token: i0.ComponentFactoryResolver }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: TooltipDirective, selector: "[tooltip], [tooltipHtml]", inputs: { content: ["tooltip", "content"], htmlContent: ["tooltipHtml", "htmlContent"], placement: ["tooltipPlacement", "placement"], isOpen: ["tooltipIsOpen", "isOpen"], enable: ["tooltipEnable", "enable"], animation: ["tooltipAnimation", "animation"], appendToBody: ["tooltipAppendToBody", "appendToBody"], popupClass: ["tooltipClass", "popupClass"], tooltipContext: "tooltipContext", delay: ["tooltipPopupDelay", "delay"], hasCloseBtn: ["tooltipHasClose", "hasCloseBtn"], autoFocus: ["tooltipAutoFocus", "autoFocus"], openOnFocus: ["tooltipOpenOnFocus", "openOnFocus"] }, outputs: { tooltipStateChanged: "tooltipStateChanged" }, host: { listeners: { "focus": "onFocus($event)", "click": "show()" } }, exportAs: ["tooltip"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[tooltip], [tooltipHtml]',
                    exportAs: 'tooltip'
                }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }, { type: i1$1.UxHelper }, { type: i0.ComponentFactoryResolver }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { content: [{
                type: Input,
                args: ['tooltip']
            }], htmlContent: [{
                type: Input,
                args: ['tooltipHtml']
            }], placement: [{
                type: Input,
                args: ['tooltipPlacement']
            }], isOpen: [{
                type: Input,
                args: ['tooltipIsOpen']
            }], enable: [{
                type: Input,
                args: ['tooltipEnable']
            }], animation: [{
                type: Input,
                args: ['tooltipAnimation']
            }], appendToBody: [{
                type: Input,
                args: ['tooltipAppendToBody']
            }], popupClass: [{
                type: Input,
                args: ['tooltipClass']
            }], tooltipContext: [{
                type: Input,
                args: ['tooltipContext']
            }], delay: [{
                type: Input,
                args: ['tooltipPopupDelay']
            }], hasCloseBtn: [{
                type: Input,
                args: ['tooltipHasClose']
            }], autoFocus: [{
                type: Input,
                args: ['tooltipAutoFocus']
            }], openOnFocus: [{
                type: Input,
                args: ['tooltipOpenOnFocus']
            }], tooltipStateChanged: [{
                type: Output
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], show: [{
                type: HostListener,
                args: ['click']
            }] } });

/* tslint:enable */
class UxdTooltipDirective extends TooltipDirective {
    constructor(viewContainerRef, componentsHelper, componentFactoryResolver, changeDetector) {
        super(viewContainerRef, componentsHelper, componentFactoryResolver, changeDetector);
        this.viewContainerRef = viewContainerRef;
        this.componentsHelper = componentsHelper;
        this.componentFactoryResolver = componentFactoryResolver;
        this.changeDetector = changeDetector;
        this.definition = new TooltipDef();
    }
    ngOnInit() {
        if (this.definition.content) {
            this.content = this.definition.content;
        }
        if (this.definition.htmlContent) {
            this.htmlContent = this.definition.htmlContent;
        }
        if (this.definition.placement) {
            this.placement = this.definition.placement;
        }
        if (this.definition.isOpen) {
            this.isOpen = this.definition.isOpen;
        }
        if (this.definition.popupClass) {
            this.popupClass = this.definition.popupClass;
        }
        if (this.definition.tooltipContext) {
            this.tooltipContext = this.definition.tooltipContext;
        }
        if (this.definition.delay) {
            this.delay = this.definition.delay;
        }
        if (typeof this.definition.hasCloseBtn !== 'undefined') {
            this.hasCloseBtn = this.definition.hasCloseBtn;
        }
        if (this.definition.autoFocus === false) {
            this.autoFocus = this.definition.autoFocus;
        }
        if (this.definition.openOnFocus === true) {
            this.openOnFocus = this.definition.openOnFocus;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UxdTooltipDirective, deps: [{ token: i0.ViewContainerRef }, { token: i1$1.UxHelper }, { token: i0.ComponentFactoryResolver }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: UxdTooltipDirective, selector: "[data-uxd-tooltip-dir]", inputs: { definition: "definition" }, exportAs: ["tooltip"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UxdTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[data-uxd-tooltip-dir]',
                    exportAs: 'tooltip'
                }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }, { type: i1$1.UxHelper }, { type: i0.ComponentFactoryResolver }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { definition: [{
                type: Input
            }] } });

/**
 * Animations used by AntTooltip.
 */
const antTooltipAnimations = {
    /** Animation that transitions a tooltip in and out. */
    tooltipState: trigger('state', [
        state('initial, void, hidden', style({ opacity: 0, transform: 'scale(0)' })),
        state('visible', style({ transform: 'scale(1)' })),
        transition('* => visible', animate('200ms cubic-bezier(0, 0, 0.2, 1)', keyframes([style({ opacity: 0, transform: 'scale(0)', offset: 0 }), style({ opacity: 0.5, transform: 'scale(0.99)', offset: 0.5 }), style({ opacity: 1, transform: 'scale(1)', offset: 1 })]))),
        transition('* => hidden', animate('100ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 0 })))
    ])
};

/** Type describing the allowed values for a number input */
/** CSS class that will be attached to the overlay panel. */
const TOOLTIP_PANEL_CLASS = 'ant-tooltip-panel';
/** CSS class that will be attached to the overlay backdrop */
const TOOLTIP_BACKDROP_CLASS = 'ant-tooltip-backdrop';
/** CSS Class that will be attached to the body element */
const TOOLTIP_SCROLL_BLOCK_CLASS = 'ant-scroll-block';
/** Options used to bind passive event listeners. */
const passiveListenerOptions = normalizePassiveListenerOptions({ passive: true });
/** Creates an error to be thrown if the user supplied an invalid tooltip position. */
function getTooltipInvalidPositionError(position) {
    return Error(`Tooltip position "${position}" is invalid.`);
}
/** Injection token that determines the scroll handling while a tooltip is visible. */
const TOOLTIP_SCROLL_STRATEGY = new InjectionToken('ant-tooltip-scroll-strategy');
/** Factory that defines what the overlay should do when the user scrolls */
function TOOLTIP_SCROLL_STRATEGY_FACTORY(overlay) {
    return () => overlay.scrollStrategies.reposition();
}
const TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER = {
    provide: TOOLTIP_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: TOOLTIP_SCROLL_STRATEGY_FACTORY
};

/**
 * Directive that attaches a tooltip to the host element. Animates the showing and
 * hiding of a tooltip provided position (defaults to above the element).
 */
class AntTooltipDirective {
    get position() {
        return this._position;
    }
    set position(value) {
        if (value !== this._position) {
            this._position = value;
            if (this._overlayRef) {
                this._updatePosition();
                if (this._tooltipInstance) {
                    this._tooltipInstance.show(0);
                }
                this._overlayRef.updatePosition();
            }
        }
    }
    get message() {
        return this._message;
    }
    set message(value) {
        // If the message is not a string (e.g. number), convert it to a string and trim it.
        this._message = value != null ? `${value}`.trim() : '';
        if (this._tooltipInstance) {
            this._tooltipInstance._message = this.message;
        }
    }
    get closeText() {
        return this._closeText;
    }
    set closeText(value) {
        // If the message is not a string (e.g. number), convert it to a string and trim it.
        this._closeText = value != null ? `${value}`.trim() : '';
        if (this._tooltipInstance) {
            this._tooltipInstance._closeText = this.closeText;
        }
    }
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        // If tooltip is disabled, hide immediately.
        if (this._disabled) {
            this.hide(0);
        }
    }
    get tooltipClass() {
        return this._tooltipClass;
    }
    set tooltipClass(value) {
        this._tooltipClass = value;
        if (this._tooltipInstance) {
            this._setTooltipClass(this._tooltipClass);
        }
    }
    constructor(_overlay, _element, _viewContainerRef, _ngZone, scrollStrategy, _breakpointObserver, _renderer, _document, _liveAnnouncer) {
        this._overlay = _overlay;
        this._element = _element;
        this._viewContainerRef = _viewContainerRef;
        this._ngZone = _ngZone;
        this._breakpointObserver = _breakpointObserver;
        this._renderer = _renderer;
        this._document = _document;
        this._liveAnnouncer = _liveAnnouncer;
        /** The message to be displayed in the tooltip */
        this._message = '';
        /** The message to be displayed in the tooltip */
        this._closeText = '';
        /** Disables the display of the tooltip. */
        this._disabled = false;
        /** The default delay in ms before showing the tooltip after show is called */
        this.showDelay = 0;
        /** The default delay in ms before hiding the tooltip after hide is called */
        this.hideDelay = 0;
        /** Emits an event each time the tooltip is toggled; hook for developers to use */
        this.toggleEvent = new EventEmitter();
        /** Manually-bound passive event listeners. */
        this._passiveListeners = new Map();
        /** Emits when the component is destroyed. */
        this._destroyed = new Subject();
        /** Observer for detachments */
        this._$detachObserver = new Subject();
        /** String containing the mobile viewport breakpoint */
        this.MOBILE = '(max-width: 480px)';
        /** Checks if the viewport is mobile size on initialization */
        this._isMobile = this._breakpointObserver.isMatched(this.MOBILE);
        /** Stream that emits when mobile threshold is crossed */
        this._isHandset = this._breakpointObserver.observe(this.MOBILE);
        /** Allows the user to define the position of the tooltip relative to the parent element */
        this._position = 'above';
        this._scrollStrategy = scrollStrategy;
        /** Pre-render to detect content change for aria-live announcer :: accessibility */
        this._liveAnnouncer.announce('');
    }
    /** Shows the tooltip after the delay in ms, defaults to tooltip-delay-show or 0ms if no input */
    show(delay = this.showDelay) {
        if (this.disabled || !this.message || (this._isTooltipVisible() && !this._tooltipInstance._showTimeoutId && !this._tooltipInstance._hideTimeoutId)) {
            return;
        }
        // Save previous element to come back to for mobile
        this._savePreviouslyFocusedElement();
        // Check if the viewport is equal to or less than 480px
        this._isHandset.subscribe((viewport) => {
            this._isMobile = viewport.matches;
        });
        const overlayRef = this._createOverlay();
        this._detach();
        this._portal = this._portal || new ComponentPortal(AntTooltipComponent, this._viewContainerRef);
        this._tooltipInstance = overlayRef.attach(this._portal).instance;
        this._tooltipInstance
            .afterHidden()
            .pipe(takeUntil(this._destroyed))
            .subscribe(() => {
            this._detach();
            this.toggleEvent.emit(false); // Emit false whenever it closes
        });
        this._setTooltipClass(this._tooltipClass);
        this._updateTooltipMessage();
        this._instantiateTooltipInstance();
        this._$detachObserver.next(true);
        this._tooltipInstance.show(delay);
        this.toggleEvent.emit(true); // Emit true for open
    }
    /** Helper function that passes properties to the child component instance */
    _instantiateTooltipInstance() {
        if (!this._tooltipInstance) {
            return;
        }
        if (this.closeText === '') {
            this.closeText = 'Close';
        }
        this._tooltipInstance._closeText = this._closeText;
        this._tooltipInstance._isHandset = this._isMobile;
        this._tooltipInstance._$detachObserver = this._$detachObserver;
        this._tooltipInstance._previousElemFocus = this._previousElemFocus;
        this._tooltipInstance.document = this._document;
    }
    /** Close the tooltip on viewport resize */
    onViewportResize(event) {
        this.hide(this.hideDelay);
    }
    onFocusOut(event) {
        if (!this._isMobile) {
            this.hide(this.hideDelay);
        }
    }
    OnEscapeKeypress(event) {
        /** Second flag in if statement is for IE */
        if (event.key === 'Escape' || event.key === 'Esc') {
            this.hide(0);
        }
    }
    /** Hides the tooltip after the delay in ms, defaults to tooltip-delay-hide or 0ms if no input */
    hide(delay = this.hideDelay) {
        if (this._tooltipInstance) {
            this._tooltipInstance.hide(delay);
            this._$detachObserver.next(false);
        }
    }
    /** Shows/hides the tooltip */
    toggle() {
        this._isTooltipVisible() ? this.hide() : this.show();
    }
    /** Updates the tooltip message and repositions the overlay according to the new message length */
    _updateTooltipMessage() {
        // Must wait for the message to be painted to the tooltip so that the overlay can properly
        // calculate the correct positioning based on the size of the text.
        if (this._tooltipInstance) {
            this._tooltipInstance._message = this.message;
            this._tooltipInstance._markForCheck();
            this._ngZone.onMicrotaskEmpty
                .asObservable()
                .pipe(take(1), takeUntil(this._destroyed))
                .subscribe(() => {
                if (this._tooltipInstance) {
                    this._overlayRef.updatePosition();
                }
            });
        }
    }
    /** Updates the tooltip class */
    _setTooltipClass(tooltipClass) {
        if (this._tooltipInstance) {
            this._tooltipInstance.tooltipClass = tooltipClass;
            this._tooltipInstance._markForCheck();
        }
    }
    /** Create the overlay config and position strategy */
    _createOverlay() {
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
        if (this._isMobile) {
            // Builds the position for the overlay by vertically and horizontally centering the panel
            let strategy = this._overlay
                .position()
                .global()
                .centerVertically()
                .centerHorizontally();
            // Configuration for mobile tooltips: apply cdk backdrop classes, max-width
            const mobileConfig = new OverlayConfig({
                positionStrategy: strategy,
                panelClass: TOOLTIP_PANEL_CLASS,
                direction: 'ltr',
                hasBackdrop: true,
                minWidth: 290,
                disposeOnNavigation: true
            });
            mobileConfig.backdropClass = TOOLTIP_BACKDROP_CLASS;
            this._overlayRef = this._overlay.create(mobileConfig);
        }
        else {
            const strategy = this._overlay
                .position()
                .flexibleConnectedTo(this._element)
                .withTransformOriginOn('.tooltip-message')
                .withFlexibleDimensions(false)
                .withViewportMargin(15);
            // Create connected position strategy that listens for scroll events to reposition.
            strategy.positionChanges.pipe(takeUntil(this._destroyed)).subscribe((change) => {
                if (this._tooltipInstance) {
                    if (change.scrollableViewProperties.isOverlayClipped && this._tooltipInstance.isVisible()) {
                        // After position changes occur and the overlay is clipped by
                        // a parent scrollable then close the tooltip.
                        this._ngZone.run(() => this.hide(0));
                    }
                }
            });
            this._overlayRef = this._overlay.create({
                direction: 'ltr',
                positionStrategy: strategy,
                panelClass: TOOLTIP_PANEL_CLASS,
                scrollStrategy: this._scrollStrategy()
            });
            this._updatePosition();
        }
        this._overlayRef
            .detachments()
            .pipe(takeUntil(this._destroyed))
            .subscribe(() => this._detach());
        return this._overlayRef;
    }
    /** Updates the position of the current tooltip. */
    _updatePosition() {
        const position = this._overlayRef.getConfig().positionStrategy;
        const origin = this._getOrigin();
        const overlay = this._getOverlayPosition();
        position.withPositions([{ ...origin.main, ...overlay.main }, { ...origin.fallback, ...overlay.fallback }]);
    }
    /**
     * Returns the origin position and a fallback position based on the user's position preference.
     * The fallback position is the inverse of the origin (e.g. `'below' -> 'above'`).
     */
    _getOrigin() {
        const position = this.position;
        let originPosition;
        if (position === 'above' || position === 'below') {
            originPosition = { originX: 'center', originY: position === 'above' ? 'top' : 'bottom' };
        }
        else if (position === 'before') {
            originPosition = { originX: 'start', originY: 'center' };
        }
        else if (position === 'after') {
            originPosition = { originX: 'end', originY: 'center' };
        }
        else {
            throw getTooltipInvalidPositionError(position);
        }
        const { x, y } = this._invertPosition(originPosition.originX, originPosition.originY);
        return {
            main: originPosition,
            fallback: { originX: x, originY: y }
        };
    }
    /** Returns the overlay position and a fallback position based on the user's preference */
    _getOverlayPosition() {
        const position = this.position;
        let overlayPosition;
        if (position === 'above') {
            overlayPosition = { overlayX: 'center', overlayY: 'bottom' };
        }
        else if (position === 'below') {
            overlayPosition = { overlayX: 'center', overlayY: 'top' };
        }
        else if (position === 'before') {
            overlayPosition = { overlayX: 'end', overlayY: 'center' };
        }
        else if (position === 'after') {
            overlayPosition = { overlayX: 'start', overlayY: 'center' };
        }
        else {
            throw getTooltipInvalidPositionError(position);
        }
        const { x, y } = this._invertPosition(overlayPosition.overlayX, overlayPosition.overlayY);
        return {
            main: overlayPosition,
            fallback: { overlayX: x, overlayY: y }
        };
    }
    /** Inverts an overlay position. */
    _invertPosition(x, y) {
        if (this.position === 'above' || this.position === 'below') {
            if (y === 'top') {
                y = 'bottom';
            }
            else if (y === 'bottom') {
                y = 'top';
            }
        }
        else {
            if (x === 'end') {
                x = 'start';
            }
            else if (x === 'start') {
                x = 'end';
            }
        }
        return { x, y };
    }
    /** Detaches the currently-attached tooltip. */
    _detach() {
        if (this._overlayRef && this._overlayRef.hasAttached()) {
            this._overlayRef.detach();
        }
        this._tooltipInstance = null;
    }
    /** Returns true if the tooltip is currently visible to the user */
    _isTooltipVisible() {
        return !!this._tooltipInstance && this._tooltipInstance.isVisible();
    }
    /**
     * Saves a reference to the element having focus just prior
     * to the modal being opened.
     * When the modal is closed, focus should return to this element.
     */
    _savePreviouslyFocusedElement() {
        if (!this._document) {
            return;
        }
        this._previousElemFocus = this._document.activeElement;
    }
    /** Dispose of the overlay in use and destroy event listeners */
    ngOnDestroy() {
        const nativeElement = this._element.nativeElement;
        clearTimeout(this._touchstartTimeout);
        if (this._overlayRef) {
            this._overlayRef.dispose();
            this._tooltipInstance = null;
        }
        // Clean up the event listeners set in the constructor
        this._passiveListeners.forEach((listener, event) => {
            nativeElement.removeEventListener(event, listener, passiveListenerOptions);
        });
        this._passiveListeners.clear();
        this._destroyed.next();
        this._destroyed.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTooltipDirective, deps: [{ token: i1$2.Overlay }, { token: i0.ElementRef }, { token: i0.ViewContainerRef }, { token: i0.NgZone }, { token: TOOLTIP_SCROLL_STRATEGY }, { token: i2.BreakpointObserver }, { token: i0.Renderer2 }, { token: DOCUMENT, optional: true }, { token: i3.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AntTooltipDirective, selector: "[ant-tooltip]", inputs: { position: "position", message: "message", closeText: ["closeButtonText", "closeText"], disabled: "disabled", tooltipClass: "tooltipClass", showDelay: "showDelay", hideDelay: "hideDelay" }, outputs: { toggleEvent: "toggle" }, host: { listeners: { "window:resize": "onViewportResize($event)", "focusout": "onFocusOut($event)", "keydown.escape": "OnEscapeKeypress($event)" } }, providers: [TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER], exportAs: ["antTooltip"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ant-tooltip]',
                    exportAs: 'antTooltip',
                    providers: [TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER]
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Overlay }, { type: i0.ElementRef }, { type: i0.ViewContainerRef }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TOOLTIP_SCROLL_STRATEGY]
                }] }, { type: i2.BreakpointObserver }, { type: i0.Renderer2 }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i3.LiveAnnouncer }]; }, propDecorators: { position: [{
                type: Input,
                args: ['position']
            }], message: [{
                type: Input,
                args: ['message']
            }], closeText: [{
                type: Input,
                args: ['closeButtonText']
            }], disabled: [{
                type: Input,
                args: ['disabled']
            }], tooltipClass: [{
                type: Input,
                args: ['tooltipClass']
            }], showDelay: [{
                type: Input,
                args: ['showDelay']
            }], hideDelay: [{
                type: Input,
                args: ['hideDelay']
            }], toggleEvent: [{
                type: Output,
                args: ['toggle']
            }], onViewportResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }], OnEscapeKeypress: [{
                type: HostListener,
                args: ['keydown.escape', ['$event']]
            }] } });
/**
 * Internal component that wraps the tooltip's content.
 */
class AntTooltipComponent {
    get document() {
        return this._document;
    }
    set document(document) {
        this._document = document;
    }
    constructor(_changeDetectorRef, _liveAnnouncer, _renderer) {
        this._changeDetectorRef = _changeDetectorRef;
        this._liveAnnouncer = _liveAnnouncer;
        this._renderer = _renderer;
        /** Property watched by the animation framework to show or hide the tooltip */
        this._visibility = 'initial';
        /** Property managing the show or hide of mobile tooltip popup */
        this._mobileVisibility = 'initial';
        /** Observer property storing when the overlay detaches */
        this._$detachObserver = new Subject();
        /** Whether interactions on the page should close the tooltip */
        this._closeOnInteraction = false;
        /** Subject for notifying that the tooltip has been hidden from the view */
        this._onHide = new Subject();
    }
    /**
     * Shows the tooltip with an animation originating from the provided origin
     * @param delay Amount of milliseconds to the delay showing the tooltip.
     */
    show(delay) {
        // Cancel the delayed hide if it is scheduled
        if (this._hideTimeoutId) {
            clearTimeout(this._hideTimeoutId);
            this._hideTimeoutId = null;
        }
        // Body interactions should cancel the tooltip if there is a delay in showing.
        this._closeOnInteraction = true;
        this._showTimeoutId = window.setTimeout(() => {
            // Handles the show part of the tooltip overlay
            this._visibility = 'visible';
            if (this._isHandset) {
                this._mobileVisibility = 'visible';
                // Block background scrolling when mobile panel is opened
                this._renderer.addClass(this._document.body, TOOLTIP_SCROLL_BLOCK_CLASS);
                this._createFocusTrap();
            }
            this._showTimeoutId = null;
            // Screenreader announcer for when the tooltip message is visually shown
            this._liveAnnouncer.announce(this._message);
            // Mark for check so if any parent component has set the
            // ChangeDetectionStrategy to OnPush it will be checked anyways
            this._markForCheck();
        }, delay);
    }
    /**
     * Begins the animation to hide the tooltip after the provided delay in ms.
     * @param delay Amount of milliseconds to delay showing the tooltip.
     */
    hide(delay) {
        // Cancel the delayed show if it is scheduled
        if (this._showTimeoutId) {
            clearTimeout(this._showTimeoutId);
            this._showTimeoutId = null;
        }
        this._hideTimeoutId = window.setTimeout(() => {
            // Handles the hidden part of the tooltip overlay
            this._visibility = 'hidden';
            this._mobileVisibility = 'hidden';
            // Remove scroll block class if attached
            this._renderer.removeClass(this._document.body, TOOLTIP_SCROLL_BLOCK_CLASS);
            this._hideTimeoutId = null;
            // Clear aria-live messages to prevent further announcements until called upon intentionally
            this._liveAnnouncer.clear();
            // Mark for check so if any parent component has set the
            // ChangeDetectionStrategy to OnPush it will be checked anyways
            this._markForCheck();
        }, delay);
        if (this._isHandset) {
            this._previousElemFocus.focus();
        }
    }
    /** Returns an observable that notifies when the tooltip has been hidden from view. */
    afterHidden() {
        return this._onHide.asObservable();
    }
    /** Whether the tooltip is being displayed. */
    isVisible() {
        return this._visibility === 'visible';
    }
    _animationStart() {
        this._closeOnInteraction = false;
    }
    _animationDone(event) {
        const toState = event.toState;
        if (toState === 'hidden' && !this.isVisible()) {
            this._onHide.next();
        }
        if (toState === 'visible' || toState === 'hidden') {
            this._closeOnInteraction = true;
        }
    }
    /** Creates a focus trap inside the tooltip panel so users cannot SHIFT+TAB or
     *  SHIFT away from the tooltip panel and lose their place
     */
    _createFocusTrap() {
        if (this.messageRef && this.messageRef.nativeElement) {
            const container = this.messageRef.nativeElement;
            focusTrap(container, this._$detachObserver);
            const [first, last] = getFocusableBoundaryElements(container);
            first.focus();
        }
    }
    ngOnDestroy() {
        this._onHide.complete();
    }
    /**
     * Interactions on the HTML body should close the tooltip immediately as defined in the
     * material design spec.
     */
    _handleBodyInteraction() {
        if (this._closeOnInteraction) {
            this.hide(0);
        }
    }
    /**
     * Marks that the tooltip needs to be checked in the next change detection run.
     * Mainly used for rendering the initial text before positioning a tooltip, which
     * can be problematic in components with OnPush change detection.
     */
    _markForCheck() {
        this._changeDetectorRef.markForCheck();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTooltipComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i3.LiveAnnouncer }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AntTooltipComponent, selector: "ant-tooltip-component", host: { listeners: { "body:click": "this._handleBodyInteraction()" }, properties: { "style.zoom": "_visibility === \"visible\" ? 1 : null" } }, viewQueries: [{ propertyName: "messageRef", first: true, predicate: ["messageElem"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"ant-tooltip-message\" #messageElem [ngClass]=\"tooltipClass\"\n  [@state]=\"_visibility\"\n  (@state.start)=\"_animationStart()\"\n  (@state.done)=\"_animationDone($event)\">\n  <span [innerText]=\"_message\"></span>\n  <div class=\"ant-tooltip-close-button\" [@state]=\"_mobileVisibility\">\n    <button class=\"btn btn-secondary\" name=\"close\" (click)=\"hide(0)\" [innerText]=\"_closeText\"></button>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], animations: [antTooltipAnimations.tooltipState], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ant-tooltip-component', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, animations: [antTooltipAnimations.tooltipState], host: {
                        // Forces the element to have a layout in IE and Edge. This fixes issues where the element
                        // won't be rendered if the animations are disabled or there is no web animations polyfill.
                        '[style.zoom]': '_visibility === "visible" ? 1 : null',
                        // Listens for click on the body element
                        '(body:click)': 'this._handleBodyInteraction()'
                    }, template: "<div class=\"ant-tooltip-message\" #messageElem [ngClass]=\"tooltipClass\"\n  [@state]=\"_visibility\"\n  (@state.start)=\"_animationStart()\"\n  (@state.done)=\"_animationDone($event)\">\n  <span [innerText]=\"_message\"></span>\n  <div class=\"ant-tooltip-close-button\" [@state]=\"_mobileVisibility\">\n    <button class=\"btn btn-secondary\" name=\"close\" (click)=\"hide(0)\" [innerText]=\"_closeText\"></button>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i3.LiveAnnouncer }, { type: i0.Renderer2 }]; }, propDecorators: { messageRef: [{
                type: ViewChild,
                args: ['messageElem', { static: true }]
            }] } });

class TooltipModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: TooltipModule, declarations: [UxdTooltipDirective, TooltipDirective, TooltipContainerComponent, AntTooltipDirective, AntTooltipComponent], imports: [CommonModule, OverlayModule], exports: [UxdTooltipDirective, TooltipDirective, TooltipContainerComponent, AntTooltipDirective, AntTooltipComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TooltipModule, imports: [CommonModule, OverlayModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule],
                    declarations: [UxdTooltipDirective, TooltipDirective, TooltipContainerComponent, AntTooltipDirective, AntTooltipComponent],
                    exports: [UxdTooltipDirective, TooltipDirective, TooltipContainerComponent, AntTooltipDirective, AntTooltipComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AntTooltipComponent, AntTooltipDirective, PositionService, TooltipContainerComponent, TooltipDirective, TooltipModule, TooltipOptions, UxdTooltipDirective, positionService };
